import * as React from "react";
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import Botao from "../../../componentes/Botao";
import { CarrinhoProdutoDTO, ProdutoDTO } from "../../../DTOs/produtoDTO";
import api from "../../../service/api";
import { useToast } from "../../../hooks/toast";
import Pagination from "../../../componentes/Paginacao/Pagination";
import { ContainerPagination } from "../../../componentes/Grid/styles";
import { Container } from "../styles";
import {
  CloseButton,
  ContainerFlutuante,
  FormPesquisa,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tabela,
} from "./styles";
import { ProdutoFiltro } from "../../../DTOs/ProdutoFiltro";
import userContext from "../../../Context/UserContext";

const ListaProdutosAdm: React.FC = () => {
  const [listaCompleta, setListaCompleta] = useState<ProdutoDTO[]>([]);
  const [busca, setBusca] = useState<string>("");
  const [carregando, setCarregando] = useState<boolean>(true);
  const [modalAberto, setModalAberto] = useState<boolean>(false);
  const [produtoSelecionado, setProdutoSelecionado] =
    useState<ProdutoDTO | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  const itensPerPage = 20;
  const searchParams = new URLSearchParams(location.search);
  const currentPageZeroBased = (Number(searchParams.get("page")) || 1) - 1;
  const currentlyUser = useContext(userContext);

  const carregarDadosDoBanco = useCallback(
    async (termo: string) => {
      setCarregando(true);
      try {
        const requestData: ProdutoFiltro = {
          Titulo: "ConsultaCapas",
          Pesquisa: termo,
          Codalmoxarifado: currentlyUser.user.Codalmoxarifado,
          Codempresa: currentlyUser.user.Codempresa,
          Codoperacao: currentlyUser.user.Codoperacao,
          Codempresacli: currentlyUser.user.Codempresacli,
          Codcliente: currentlyUser.user.Codcliente,
        };

        const response = await api.post<ProdutoDTO[]>(
          "api/business/produtocapa",
          requestData,
        );
        const produtos = response.data;
        setListaCompleta(produtos);

        history.push("?page=1");
      } catch (error: any) {
        setListaCompleta([]);
        if (error.response?.status !== 404) {
          addToast({ type: "error", title: "Erro ao buscar produtos" });
        }
      } finally {
        setCarregando(false);
      }
    },
    [addToast, history],
  );

  useEffect(() => {
    carregarDadosDoBanco("");
  }, [carregarDadosDoBanco]);

  const { itensExibidos, totalPages } = useMemo(() => {
    const total = Math.ceil(listaCompleta.length / itensPerPage);
    const inicio = currentPageZeroBased * itensPerPage;
    const fim = inicio + itensPerPage;

    return {
      itensExibidos: listaCompleta.slice(inicio, fim),
      totalPages: total || 1,
    };
  }, [listaCompleta, currentPageZeroBased]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    carregarDadosDoBanco(busca);
  };

  const handlePageChange = (index: number) => {
    history.push(`?page=${index + 1}`);
    window.scrollTo(0, 0);
  };

  const abrirModal = (produto: ProdutoDTO) => {
    setProdutoSelecionado(produto);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setProdutoSelecionado(null);
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !produtoSelecionado) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "codProduto",
      produtoSelecionado.Codproduto?.toString() || "",
    );

    try {
      const responseUpload = await api.post(
        "api/produto/capaupload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      const novoPath = responseUpload.data;
      const nomeArquivo = novoPath.split(/[\\/]/).pop();

      await api.post(
        `api/produto/${produtoSelecionado.Codproduto}/atualizacapa`,
        {
          pathimagem: nomeArquivo,
        },
      );

      setListaCompleta((prev) =>
        prev.map((p) =>
          p.Codproduto === produtoSelecionado.Codproduto
            ? { ...p, Pathimg: nomeArquivo }
            : p,
        ),
      );

      addToast({ type: "success", title: "Capa atualizada com sucesso" });
      fecharModal();

      await carregarDadosDoBanco(busca);
    } catch (error) {
      addToast({ type: "error", title: "Erro ao atualizar capa" });
    }
  };

  return (
    <Container>
      <ContainerFlutuante>
        <FormPesquisa onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Pesquisar por Título"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Botao
            type="submit"
            containerStyle={{
              backgroundColor: "#ED3237",
              borderColor: "#fff",
              color: "#fff",
              borderWidth: 1,
              borderStyle: "solid",
              padding: 10,
              margin: 0,
              height: "inherit",
              fontWeight: "normal",
              fontSize: 16,
            }}
          >
            Pesquisar
          </Botao>
        </FormPesquisa>

        {carregando ? (
          <p style={{ padding: 20 }}>Carregando produtos...</p>
        ) : listaCompleta.length === 0 ? (
          <p style={{ padding: 20 }}>Nenhum produto encontrado.</p>
        ) : (
          <div>
            <Tabela>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Título</th>
                  <th>ISBN</th>
                  <th>Editora</th>
                </tr>
              </thead>
              <tbody>
                {itensExibidos.map((item) => (
                  <tr key={item.Codproduto}>
                    <td>{item.Codproduto}</td>
                    <td
                      onClick={() => abrirModal(item)}
                      style={{ cursor: "pointer" }}
                    >
                      {item.Titulo}
                    </td>
                    <td>{item.Isbn}</td>
                    <td>{item.Editora}</td>
                  </tr>
                ))}
              </tbody>
            </Tabela>

            <ContainerPagination>
              <Pagination
                pages={totalPages}
                setCurrentPaged={handlePageChange}
                currentPage={currentPageZeroBased}
              />
            </ContainerPagination>
          </div>
        )}
      </ContainerFlutuante>

      {modalAberto && produtoSelecionado && (
        <ModalOverlay
          onClick={fecharModal}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Detalhes do Produto: {produtoSelecionado.Codproduto}</h2>
              <CloseButton onClick={fecharModal}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <p>
                <strong>Título:</strong> {produtoSelecionado.Titulo}
              </p>
              <p>
                <strong>ISBN:</strong> {produtoSelecionado.Isbn || "N/A"}
              </p>
              <p>
                <strong>Capa:</strong>{" "}
                {produtoSelecionado.Pathimagem || "Nenhuma imagem"}
              </p>

              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleUpload}
                accept="image/*"
              />

              <Botao
                onClick={() => fileInputRef.current?.click()}
                containerStyle={{
                  borderWidth: 1,
                  borderStyle: "solid",
                  padding: 5,
                  margin: 0,
                  height: "inherit",
                  fontWeight: "normal",
                  fontSize: 12,
                }}
              >
                Subir Imagem
              </Botao>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ListaProdutosAdm;